import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import api from "../api/api";
import { Helmet } from "react-helmet-async";
import FeaturedLoader from "../components/Loaders/FeaturedLoader";


export default function Blogs() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");

  useEffect(() => {
    const fetchBlogs = async () => {
      setLoading(true);
      try {
        const res = await api.get("/blogs");
        const data = res.data;

        setBlogs(data);

        const uniqueCategories = [
          ...new Map(
            data
              .filter((b) => b.category)
              .map((b) => [b.category.id, b.category])
          ).values(),
        ];

        setCategories(uniqueCategories);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  const filteredBlogs = blogs.filter((blog) => {
    const matchesSearch = blog.title
      .toLowerCase()
      .includes(search.toLowerCase());

    const matchesCategory = selectedCategory
      ? blog.category?.id === Number(selectedCategory)
      : true;

    return matchesSearch && matchesCategory;
  });

  return (
    <>
      <Helmet>
        {/* Basic SEO */}
        <title>Travel Blog | UAE Discounted Hotels</title>

        <meta
          name="description"
          content="Explore the UAE Discounted Hotels blog for the latest hotel deals, travel guides, destination tips, staycation ideas, and budget-friendly accommodation across the UAE."
        />

        <meta
          name="keywords"
          content="UAE travel blog, Dubai travel blog, hotel deals UAE, discounted hotels, Dubai hotels, Abu Dhabi hotels, UAE staycation, travel tips UAE, budget hotels UAE"
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="UAE Discounted Hotels" />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://uaediscountedhotels.com/blogs"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="UAE Discounted Hotels" />
        <meta property="og:locale" content="en_US" />

        <meta
          property="og:title"
          content="Travel Blog | UAE Discounted Hotels"
        />

        <meta
          property="og:description"
          content="Read travel guides, hotel recommendations, destination insights, and the latest hotel deals across Dubai, Abu Dhabi, Sharjah, and the UAE."
        />

        <meta
          property="og:url"
          content="https://uaediscountedhotels.com/blogs"
        />

        <meta
          property="og:image"
          content="https://uaediscountedhotels.com/images/default-og.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />

        <meta
          name="twitter:title"
          content="Travel Blog | UAE Discounted Hotels"
        />

        <meta
          name="twitter:description"
          content="Explore travel tips, hotel reviews, destination guides, and exclusive hotel deals throughout the UAE."
        />

        <meta
          name="twitter:image"
          content="https://uaediscountedhotels.com/images/default-og.png"
        />
      </Helmet>

      <div className="w-full bg-gray-50 dark:bg-gray-950">

        <PageHeader
          title="Travel Stories"
          subtitle="Guides, tips and real travel experiences"
        />

        <div className="container mx-auto px-4 sm:px-6 py-8 lg:py-16 grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-10">

          {/* ================= SIDEBAR ================= */}
          <aside className="hidden lg:block lg:col-span-1 space-y-6">

            {/* SEARCH */}
            <div className="bg-white dark:bg-gray-900 p-4 rounded-2xl border border-gray-200 dark:border-gray-800">
              <input
                type="text"
                placeholder="Search stories..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white"
              />
            </div>

            {/* CATEGORIES */}
            <div className="bg-white dark:bg-gray-900 p-4 rounded-2xl border border-gray-200 dark:border-gray-800">
              <h3 className="font-semibold mb-3 text-gray-900 dark:text-white">
                Categories
              </h3>

              <div className="space-y-2">

                <button
                  onClick={() => setSelectedCategory("")}
                  className={`block w-full text-left px-3 py-2 rounded-lg transition ${selectedCategory === ""
                    ? "bg-gradient-to-r from-blue-600 to-cyan-500 text-white"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                    }`}
                >
                  All
                </button>

                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`block w-full text-left px-3 py-2 rounded-lg transition ${selectedCategory === cat.id
                      ? "bg-gradient-to-r from-blue-600 to-cyan-500 text-white"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                      }`}
                  >
                    {cat.name}
                  </button>
                ))}

              </div>
            </div>

          </aside>

          {/* ================= MAIN ================= */}
          <main className="lg:col-span-3 space-y-6">

            {/* FEATURED */}
            {filteredBlogs.length > 0 && (
              <Link to={`/blogs/${filteredBlogs[0].slug}`}>
                <div className="relative h-[260px] sm:h-[320px] lg:h-[420px] rounded-2xl lg:rounded-3xl overflow-hidden shadow-xl group">

                  <img
                    src={filteredBlogs[0].thumbnail_image}
                    className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                    alt={filteredBlogs[0].title || filteredBlogs[0].name || "Blog image"}
                  />

                  <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent"></div>

                  <div className="absolute bottom-6 left-6 text-white max-w-xl">

                    <span className="text-xs px-3 py-1 rounded-full bg-gradient-to-r from-blue-600 to-cyan-500">
                      {filteredBlogs[0].category?.name}
                    </span>

                    <h1 className="text-2xl font-bold mt-2">
                      {filteredBlogs[0].title}
                    </h1>

                    <p className="text-sm text-gray-200 mt-2 line-clamp-2">
                      {filteredBlogs[0].excerpt}
                    </p>

                  </div>
                </div>
              </Link>
            )}

            {/* GRID */}
            {/* ================= MOBILE BLOG STYLE ================= */}
            <div className="block sm:hidden flex flex-col gap-6 pb-6">

              {filteredBlogs.slice(1).map((blog) => (
                <Link key={blog.id} to={`/blogs/${blog.slug}`}>

                  <div className="relative rounded-2xl overflow-hidden shadow-md">

                    {/* IMAGE */}
                    <div className="h-56 w-full">
                      <img
                        src={blog.thumbnail_image}
                        className="w-full h-full object-cover"
                        alt={blog.title}
                      />
                    </div>

                    {/* OVERLAY */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>

                    {/* TEXT */}
                    <div className="absolute bottom-4 left-4 right-4 text-white">

                      <span className="text-xs px-2 py-1 rounded-full bg-gradient-to-r from-blue-600 to-cyan-500">
                        {blog.category?.name}
                      </span>

                      <h2 className="font-semibold mt-2 line-clamp-2">
                        {blog.title}
                      </h2>

                      <p className="text-xs text-gray-200 mt-1 line-clamp-2">
                        {blog.excerpt}
                      </p>

                    </div>

                  </div>

                </Link>
              ))}
            </div>


            {/* ================= DESKTOP BLOG GRID ================= */}
            <div className="hidden sm:grid grid-cols-1 sm:grid-cols-2 gap-5">

              {filteredBlogs.slice(1).map((blog) => (
                <Link key={blog.id} to={`/blogs/${blog.slug}`}>
                  <div className="
        group bg-white dark:bg-gray-900
        border border-gray-200 dark:border-gray-800
        rounded-2xl overflow-hidden
        hover:shadow-xl transition-all duration-300
      ">

                    <div className="h-48 sm:h-44 overflow-hidden">
                      <img
                        src={blog.thumbnail_image}
                        className="w-full h-full object-cover group-hover:scale-105 transition"
                        alt={blog.title}
                      />
                    </div>

                    <div className="p-4">

                      <span className="text-xs px-2 py-1 rounded-full bg-gradient-to-r from-blue-600 to-cyan-500 text-white">
                        {blog.category?.name}
                      </span>

                      <h2 className="font-semibold mt-2 text-gray-900 dark:text-white line-clamp-2">
                        {blog.title}
                      </h2>

                      <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mt-1">
                        {blog.excerpt}
                      </p>

                      <span className="text-xs text-gray-400 mt-3 block">
                        {new Date(blog.created_at).toDateString()}
                      </span>

                    </div>

                  </div>
                </Link>
              ))}

            </div>

          </main>

        </div>
      </div>
    </>
  );
}